import { FEED_TILES, CONTACT } from "../lib/brand";
import { useReveal } from "../hooks/useReveal";

const HeartIcon = (props) => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6"
       strokeLinecap="round" strokeLinejoin="round" {...props}>
    <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z" />
  </svg>
);

const CommentIcon = (props) => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6"
       strokeLinecap="round" strokeLinejoin="round" {...props}>
    <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
  </svg>
);

const ArrowUpRight = (props) => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.4"
       strokeLinecap="round" strokeLinejoin="round" {...props}>
    <path d="M7 17 17 7" />
    <path d="M7 7h10v10" />
  </svg>
);

export const SocialFeed = () => {
  const headRef = useReveal();
  const gridRef = useReveal();

  return (
    <section
      id="feed"
      data-testid="feed-section"
      className="py-24 md:py-40 border-b border-black/100"
    >
      <div className="max-w-[1480px] mx-auto px-6 md:px-12">
        {/* Header */}
        <div
          ref={headRef}
          className="reveal flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-12 md:mb-16"
        >
          <div>
            <div className="label-eyebrow mb-4 flex items-center gap-3">
              <span className="inline-block w-1.5 h-1.5 rounded-full bg-black animate-pulse" />
              Live · From the feed
            </div>
            <h2 className="font-display text-[2.6rem] md:text-7xl leading-[0.95] tracking-[-0.015em]">
              As seen on <br />
              <a
                href={CONTACT.social.instagram}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="feed-handle-link"
                className="italic link-underline"
              >
                {CONTACT.social.instagramHandle}
              </a>
            </h2>
          </div>
          <div className="flex items-center gap-4">
            <a
              href={CONTACT.social.instagram}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="feed-follow-cta"
              className="btn-pill"
            >
              Follow on Instagram
            </a>
          </div>
        </div>

        {/* Grid */}
        <div
          ref={gridRef}
          className="reveal reveal-delay-2 grid grid-cols-2 md:grid-cols-3 gap-3 md:gap-5"
        >
          {FEED_TILES.map((tile, i) => (
            <a
              key={i}
              href={CONTACT.social.instagram}
              target="_blank"
              rel="noopener noreferrer"
              data-testid={`feed-tile-${i}`}
              className="group relative block overflow-hidden aspect-square bg-black/5"
            >
              <img
                src={tile.image}
                alt={tile.caption}
                loading="lazy"
                className="editorial-img absolute inset-0 w-full h-full object-cover"
              />

              {/* Overlay on hover */}
              <div className="absolute inset-0 bg-black/0 group-hover:bg-black/55 transition-colors duration-500 flex flex-col justify-end p-4 md:p-5 text-white opacity-0 group-hover:opacity-100">
                <p className="font-body text-sm leading-snug mb-3 max-w-[26ch]">
                  {tile.caption}
                </p>
                <div className="flex items-center gap-5 text-xs">
                  <span className="flex items-center gap-1.5">
                    <HeartIcon className="w-4 h-4" /> {tile.likes}
                  </span>
                  <span className="flex items-center gap-1.5">
                    <CommentIcon className="w-4 h-4" /> {Math.floor(tile.likes / 12)}
                  </span>
                </div>
              </div>

              {/* Corner indicator */}
              <div className="absolute top-3 right-3 w-8 h-8 rounded-full bg-white/90 text-black flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-500">
                <ArrowUpRight className="w-4 h-4" />
              </div>
            </a>
          ))}
        </div>

        {/* Socials row */}
        <div className="mt-14 md:mt-20 pt-10 border-t border-black flex flex-col md:flex-row md:items-center md:justify-between gap-6">
          <p className="font-body text-sm text-black/75 max-w-lg leading-relaxed">
            Every frame above was kept on a Prismatics rental. Tag us
            <span className="italic"> {CONTACT.social.instagramHandle} </span>
            for a chance to be featured.
          </p>
          <div className="flex flex-wrap items-center gap-6">
            <a
              href={CONTACT.social.instagram}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="feed-social-instagram"
              className="label-micro link-underline"
            >
              Instagram · {CONTACT.social.instagramHandle}
            </a>
            <a
              href={CONTACT.social.tiktok}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="feed-social-tiktok"
              className="label-micro link-underline"
            >
              TikTok · {CONTACT.social.tiktokHandle}
            </a>
            <a
              href={CONTACT.social.facebook}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="feed-social-facebook"
              className="label-micro link-underline"
            >
              Facebook · {CONTACT.social.facebookHandle}
            </a>
          </div>
        </div>
      </div>
    </section>
  );
};
